import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import {
  uploadImageToCloudinary,
  CloudinaryUploadResponse,
} from '@/services/cloudinary';
import { Upload, X, Image as ImageIcon, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface ImageUploadProps {
  onImageUpload: (imageData: CloudinaryUploadResponse) => void;
  onImageRemove: () => void;
  currentImageUrl?: string | undefined;
  className?: string;
}

const ImageUpload = ({
  onImageUpload,
  onImageRemove,
  currentImageUrl,
  className,
}: ImageUploadProps) => {
  const [isUploading, setIsUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  const { t } = useTranslation();

  const handleFileSelect = (file: File) => {
    uploadImage(file);
  };

  const uploadImage = async (file: File) => {
    setIsUploading(true);

    try {
      const imageData = await uploadImageToCloudinary(file);
      onImageUpload(imageData);

      toast({
        title: t('imageUpload.uploadSuccess'),
        description: t('imageUpload.uploadSuccessDesc'),
      });
    } catch (error) {
      console.error('Image upload error:', error);

      const errorMessage =
        error instanceof Error ? error.message : 'Failed to upload image';
      toast({
        title: t('imageUpload.uploadFailed'),
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
    // Reset input value to allow re-uploading the same file
    event.target.value = '';
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) {
      handleFileSelect(file);
    } else {
      toast({
        title: t('imageUpload.invalidFileType'),
        description: t('imageUpload.invalidFileTypeDesc'),
        variant: 'destructive',
      });
    }
  };

  const handleRemoveImage = () => {
    onImageRemove();
    toast({
      title: t('imageUpload.imageRemoved'),
      description: t('imageUpload.imageRemovedDesc'),
    });
  };

  const openFileDialog = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className={className}>
      <Label className="text-sm font-medium">
        {t('imageUpload.articleCoverImageOptional')}
      </Label>

      {currentImageUrl ? (
        <Card className="mt-2">
          <CardContent className="p-4">
            <div className="relative">
              <img
                src={currentImageUrl}
                alt={t('placeholders.articleCover')}
                className="w-full h-48 object-cover rounded-lg"
              />
              <Button
                type="button"
                variant="destructive"
                size="sm"
                className="absolute top-2 right-2"
                onClick={handleRemoveImage}
                disabled={isUploading}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card
          className={`mt-2 border-2 border-dashed transition-colors ${
            dragActive
              ? 'border-primary bg-primary/5'
              : 'border-muted-foreground/25'
          }`}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <CardContent className="p-6">
            <div className="text-center">
              <div className="flex justify-center mb-4">
                {isUploading ? (
                  <Loader2 className="h-12 w-12 text-muted-foreground animate-spin" />
                ) : (
                  <ImageIcon className="h-12 w-12 text-muted-foreground" />
                )}
              </div>

              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  {isUploading
                    ? t('imageUpload.uploadingImage')
                    : t('imageUpload.dragDropOrClick')}
                </p>

                {!isUploading && (
                  <>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={openFileDialog}
                      className="mt-2"
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      {t('imageUpload.chooseImage')}
                    </Button>

                    <p className="text-xs text-muted-foreground">
                      {t('imageUpload.supportedFormats')}
                    </p>
                  </>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/webp"
        onChange={handleFileInputChange}
        className="hidden"
        disabled={isUploading}
      />
    </div>
  );
};

export default ImageUpload;
